import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {JSONSchema7, JSONSchema7Definition} from 'json-schema';
import {firstValueFrom, Observable, Subject} from 'rxjs';
import {GlobalsService} from '../../../../src/app/services/globals.service';
import {SDKConfig} from '../../sdk.config';
import {TreeUtils} from '../../utils/tree.utils';

export interface ISubmitRequestMessages<t_Model> {
    loading: string;
    successSummary: string;
    errorSummary: string;
    successDetail: (result: t_Model) => string;
    errorDetail: (err: unknown) => string;
}

/**
 * Base component
 */
@Component({
    selector: 'app-ibase',
    template: '',
    imports: [ReactiveFormsModule]
})
export abstract class IBaseComponent implements OnInit, OnDestroy {
    protected readonly UUID_REGEX = '[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}';
    protected readonly FAKE_UUID = '00000000-0000-0000-0000-000000000000';
    protected readonly SDKConfig = SDKConfig;
    protected readonly TreeUtils = TreeUtils;

    protected readonly _destroy$ = new Subject<void>();

    // ===============================================================
    // ===============================================================

    protected globalsService: GlobalsService = inject(GlobalsService);
    protected formBuilder: FormBuilder = inject(FormBuilder);

    protected alreadyGenerateColor: number[] = [];
    protected form!: FormGroup;

    public showLoading = false;
    @Input()
    public parentShowingLoading?: boolean;
    public showError = false;
    public loadingStr: string | null = null;
    public overrideMessagesKey: string | undefined = undefined;

    // ===============================================================
    // ===============================================================

    /**
     * On init
     */
    async ngOnInit() {
        this.showLoading = true;

        try {
            await this.onReady();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'error',
                detail: 'Error while initializing component: ' + (err?.error?.message ?? err.toString())
            });

            // Log error in the console for debugging, do not remove
            console.log(err);

            this.showError = true;
        }

        this.showLoading = false;
    }
    /**
     * On destroy
     */
    async ngOnDestroy() {
        try {
            this._destroy$.next();
            this._destroy$.complete();

            await this.onDestroy();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'error',
                detail: 'Error while destroying component: ' + (err?.error?.message ?? err.toString())
            });

            // Log error in the console for debugging, do not remove
            console.log(err);

            this.showError = true;
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected abstract onReady(): Promise<void>;
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    protected abstract onDestroy(): Promise<void>;

    // ===============================================================
    // ===============================================================

    /**
     * Async functor that handle errors
     * @param functor Async functor
     */
    protected async asyncCritical(functor: () => Promise<void>) {
        try {
            await functor();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'error',
                detail: 'Error: ' + (err?.error?.message ?? err.toString())
            });

            // Log error in the console for debugging, do not remove
            console.log(err);

            this.showError = true;
        }
    }
    /**
     * Await multiple Promise/Promise functors in a parallel fashion and then await then in sequence order
     * Errot handling by asyncCritical
     * m: Loading message to show
     * a: Awaitable promise
     * f: Awaitable generator (all started at the beginning of the awaitMulti)
     * @param awaitables
     */
    protected async parallelSequencedAsyncCritical(
        awaitables: {m?: string; a?: Promise<void>; f?: () => Promise<void>}[]
    ): Promise<void> {
        await this.asyncCritical(async (): Promise<void> => {
            // Start function if any
            for (const record of awaitables) {
                if (!record.f) continue;

                record.a = record.f();
            }

            for (const record of awaitables) {
                this.loadingStr = record.m ?? null;
                if (!record.a) continue;

                await record.a;
            }
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * This function generate a color from a string. Same string will always generate the same color.
     * The color should be accessible (ie. not too dark or too bright) and clearly different from other generated colors.
     * @param str Input string
     * @param iteration Current iteration number
     * @returns HSL color in string format
     */
    // lighter tints (https://system.design.orange.com/0c1af118d/p/7059a5-colour)
    protected primaryColors: string[] = ['#B5E8F7', '#B8EBD6', '#FFE8F7', '#D9C2F0', '#FFF6B6'];

    // supporting & functionnal colours & 2 functionnal greys
    protected secondaryColors: string[] = [
        '#4BB4E6',
        '#50BE87',
        '#FFB4E6',
        '#A885D8',
        '#FFD200',
        '#CCCCCC',
        '#527EDB',
        '#32C832',
        '#FFCC00',
        '#CD3C14',
        '#CCCCCC',
        '#DDDDDD'
    ];

    protected usedColors = new Set<string>();

    protected generateColorFromString(str: string, allowReuse = false, iteration = 0): string {
        // Index generation
        const hash = str.split('').reduce((a, b) => {
            a = (a << 5) - a + b.charCodeAt(0);
            return a & a;
        }, iteration);

        // Trying primary color
        const availablePrimaryColors = this.primaryColors.filter((color) => !this.usedColors.has(color));
        if (availablePrimaryColors.length > 0) {
            const colorIndex = Math.abs(hash) % availablePrimaryColors.length;
            const selectedColor = availablePrimaryColors[colorIndex];
            if (!allowReuse) {
                this.usedColors.add(selectedColor);
            }
            return selectedColor;
        }

        // if all the primary colors are used, use secondary
        const availableSecondaryColors = this.secondaryColors.filter((color) => !this.usedColors.has(color));
        if (availableSecondaryColors.length > 0) {
            const colorIndex = Math.abs(hash) % availableSecondaryColors.length;
            const selectedColor = availableSecondaryColors[colorIndex];
            if (!allowReuse) {
                this.usedColors.add(selectedColor);
            }
            return selectedColor;
        }

        // if all colors are used but AllowReuse is true, reuse primary
        if (allowReuse && this.primaryColors.length > 0) {
            const colorIndex = Math.abs(hash) % this.primaryColors.length;
            return this.primaryColors[colorIndex];
        }

        // Fallback : default color
        return '#999999';
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get a JSONSchema7Definition as a JSONSchema7
     * @param property Input JSONSchema7Definition
     * @returns Casted value
     */
    protected asJSONProperty(property: JSONSchema7Definition): JSONSchema7 {
        return property as JSONSchema7;
    }

    /**
     * Helper function to handle request with loading and error handling
     * @param serviceFunction Function that returns an Observable
     * @param messages Messages for loading, success, and error states
     * @param extendedResultBuilder Optional function to extend the result
     * @returns Extended result or null in case of error
     */
    protected async submitRequest<t_Model, t_ResultExtended>(
        serviceFunction: () => Observable<t_Model>,
        messages: ISubmitRequestMessages<t_Model>,
        extendedResultBuilder?: ((resultCRUD: t_Model) => t_ResultExtended) | null
    ): Promise<t_ResultExtended | null> {
        try {
            this.showLoading = true;
            this.loadingStr = messages.loading;

            const result: t_Model = await firstValueFrom(serviceFunction());

            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'success',
                summary: messages.successSummary,
                detail: messages.successDetail(result)
            });

            const extendtedResult: t_ResultExtended | null = extendedResultBuilder
                ? extendedResultBuilder(result)
                : null;

            return extendtedResult;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            console.log(err);
            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'error',
                summary: messages.errorSummary,
                detail: messages.errorDetail(err)
            });
        }

        this.showLoading = false;
        return null;
    }
}
